/**
 * Serializable document slot payload for Redux / journey JSON.
 * Security-first: persist only metadata + remote preview URL / document id.
 * Do not persist inline data URLs (can trigger WAF/VAPT and oversized payloads).
 */

function pickRemotePreviewUrl(response) {
  if (!response || typeof response !== "object") return null;
  const candidates = [
    response.fileUrl,
    response.documentUrl,
    response.previewUrl,
    response.url,
    response.data?.fileUrl,
    response.data?.documentUrl,
    response.data?.previewUrl,
    response.data?.url,
  ];
  for (const u of candidates) {
    if (typeof u === "string" && /^https?:\/\//i.test(u.trim())) return u.trim();
  }
  return null;
}

function pickDocumentId(response) {
  if (!response || typeof response !== "object") return null;
  const candidates = [
    response.documentId,
    response.fileEntryId,
    response.id,
    response.data?.documentId,
    response.data?.fileEntryId,
    response.data?.id,
  ];
  for (const candidate of candidates) {
    if (
      typeof candidate === "string" ||
      typeof candidate === "number"
    ) {
      const value = String(candidate).trim();
      if (value) return value;
    }
  }
  return null;
}

/**
 * Map journey / API document metadata to preview fields used by DocumentFilePreview
 * (supports alternate keys: fileUrl, thumbnailUrl, etc.).
 * Intentionally ignores inline data URLs.
 * @param {object|null|undefined} source
 * @returns {{ previewUrl?: string, previewDataUrl?: string }}
 */
export function pickDocumentPreviewFields(source) {
  if (!source || typeof source !== "object") return {};
  const candidates = [
    source.previewUrl,
    source.documentUrl,
    source.url,
    source.fileUrl,
    source.documentFileUrl,
    source.downloadUrl,
    source.viewUrl,
    source.publicUrl,
    source.thumbnailUrl,
    source.imageUrl,
  ];
  for (const raw of candidates) {
    if (typeof raw !== "string") continue;
    const t = raw.trim();
    if (!t) continue;
    if (/^https?:\/\//i.test(t) || t.startsWith("blob:")) {
      return { previewUrl: t };
    }
    if (t.startsWith("//")) {
      return { previewUrl: `https:${t}` };
    }
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    const origin = window.location.origin;
    for (const raw of candidates) {
      if (typeof raw !== "string") continue;
      const t = raw.trim();
      if (t.startsWith("/")) {
        return { previewUrl: `${origin}${t}` };
      }
    }
  }
  return {};
}

/**
 * @param {File|Blob} file
 * @param {object|null} uploadResponse - optional API JSON after upload
 * @returns {Promise<object|null>}
 */
export async function serializeDocumentFileMetaWithImagePreview(
  file,
  uploadResponse = null,
) {
  if (!file) return null;
  const documentId = pickDocumentId(uploadResponse);
  const base = {
    __fileMeta: true,
    name: file.name || "document",
    size: file.size,
    type: file.type || "",
    lastModified: file.lastModified,
    ...(documentId ? { documentId } : {}),
  };

  const remote = pickRemotePreviewUrl(uploadResponse);
  if (remote) {
    return { ...base, previewUrl: remote };
  }
  return base;
}

/** Sync baseline when async preview is not needed (tests / legacy). */
export function toSerializableFileMetaSync(file) {
  if (!file) return null;
  return {
    __fileMeta: true,
    name: file.name,
    size: file.size,
    type: file.type,
    lastModified: file.lastModified,
  };
}

/**
 * Remove inline data-URL previews from a documents map before proposal/journey API payloads.
 * Keeps __fileMeta, previewUrl (https), and names; avoids huge JSON that can fail Object API / DB limits.
 * Redux may still hold previewDataUrl for in-session thumbnails.
 *
 * @param {Record<string, unknown>|null|undefined} snapshot
 * @returns {Record<string, unknown>|null|undefined}
 */
export function stripDocumentSnapshotForApi(snapshot) {
  if (!snapshot || typeof snapshot !== "object") return {};

  const result = {};

  Object.keys(snapshot).forEach((tab) => {
    const tabDocs = snapshot[tab];

    if (!tabDocs || typeof tabDocs !== "object") return;

    result[tab] = {};

    Object.keys(tabDocs).forEach((key) => {
      const val = tabDocs[key];

      if (!val || typeof val !== "object") return;

      result[tab][key] = {
        __fileMeta: true,
        name: val.name || null,
        size: val.size || null,
        type: val.type || null,
        lastModified: val.lastModified || null,
        previewUrl: val.previewUrl || null,
        ...(val.documentId ? { documentId: val.documentId } : {}),
      };
    });
  });

  return result;
}
